import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ActivityItem } from '@/lib/api';

interface ActivityFeedProps {
  items: ActivityItem[];
  /** Maximum items to display; defaults to 5 to match mock parity. */
  limit?: number;
}

/**
 * Format an ISO timestamp as a relative time string ("12 min ago", "1h ago",
 * "3d ago"). Used by the activity feed where exact wall-clock isn't useful.
 * Falls back to a localized date string for items older than 6 days.
 */
function relativeTime(isoString: string): string {
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) return 'just now';
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

/**
 * Human label for an activity row when the Worker gave us no body text
 * (inbound calls, missed calls, MMS, status changes). Guarantees a row never
 * renders as a bare timestamp.
 */
function fallbackLabelForType(type: string): string {
  switch (type) {
    case 'booking':
      return 'Booking confirmed';
    case 'quote':
      return 'Quote sent';
    case 'call':
      return 'Inbound call';
    default:
      return 'New activity';
  }
}

const SEEN_KEY = 'ss_activity_seen_iso';

/**
 * Read the user's "last seen activity" timestamp from localStorage. Returns
 * 0 (epoch) if never set, which means everything reads as fresh on first run.
 */
function readLastSeen(): number {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) return 0;
    const n = Date.parse(raw);
    return Number.isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
}

/**
 * ActivityFeed — list of recent ActivityItems. Each row is tappable and
 * navigates to the conversation detail when the Worker provides a
 * conversation_id (Worker v2.5.2+).
 *
 * P3.x polish (Ernie 2026-05-03):
 *   1. Rows are clickable buttons, not static divs (was missing).
 *   2. Status dot is BLUE when the item is newer than the user's last-seen
 *      timestamp (i.e. "fresh / not yet reviewed"), otherwise gray. Uses a
 *      localStorage timestamp to track per-user seen state — no server
 *      coordination needed and survives refreshes/reinstalls.
 *   3. After the user has had the feed visible for ~3s, we bump last-seen
 *      to "now" so dots fade to gray on the next mount, mirroring how
 *      Insta/Twitter handle "you've seen this story" state.
 */
export function ActivityFeed({ items, limit = 5 }: ActivityFeedProps) {
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const display = items.slice(0, limit);

  // Snapshot the last-seen timestamp at mount so dots don't flip to gray
  // mid-render when the timeout below updates localStorage. We compare
  // each item's timestamp against this snapshot for the duration of this
  // mount's render life.
  const [seenAtMount] = useState<number>(() => readLastSeen());

  // Mark "now" as the new last-seen after the user has had the list
  // visible for a moment. Next time Today mounts, anything newer than
  // this run's "now" will be fresh; anything older will be gray.
  useEffect(() => {
    if (display.length === 0) return;
    const t = window.setTimeout(() => {
      try {
        window.localStorage.setItem(SEEN_KEY, new Date().toISOString());
      } catch {
        /* ignore — private mode etc. */
      }
    }, 3000);
    return () => window.clearTimeout(t);
  }, [display.length]);

  if (display.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-ink-400 dark:text-[#64748B]">
        No activity yet today.
      </div>
    );
  }

  const handleSelect = (item: ActivityItem) => {
    if (item.conversation_id) {
      navigate(`/${slug}/conversations/${item.conversation_id}`);
    } else {
      // Fallback: the Worker didn't include a conversation_id (older
      // version). Drop the user on the conversations list rather than
      // dead-ending the tap.
      navigate(`/${slug}/conversations`);
    }
  };

  return (
    <div className="px-4">
      {display.map((it, i) => {
        const ts = Date.parse(it.timestamp_iso);
        const isFresh = !Number.isNaN(ts) && ts > seenAtMount;
        const isLast = i === display.length - 1;
        // Never render an empty row: fall back to a type-derived label when
        // the Worker gave us no body, and to the type label as a title when
        // there's no contact name either.
        const bodyText = (it.body ?? '').trim() || fallbackLabelForType(it.type);
        const titleText = it.contact_name?.trim();

        return (
          <button
            key={`${it.timestamp_iso}-${i}`}
            type="button"
            onClick={() => handleSelect(it)}
            className={[
              'flex w-full items-start gap-3 px-1 py-3 text-left',
              'transition-colors duration-150 ease-ss-standard',
              'active:bg-ink-100 dark:active:bg-[#1E293B] active:scale-[0.997]',
              'focus:outline-none focus-visible:bg-ink-100 dark:focus-visible:bg-[#1E293B]',
              isLast ? '' : 'border-b border-ink-100 dark:border-[#334155]',
            ].join(' ')}
            aria-label={
              it.contact_name
                ? `Open conversation with ${it.contact_name}`
                : 'Open conversation'
            }
          >
            {/* Dot — blue when fresh (unseen), invisible when seen so
                the list reads cleanly without trailing gray dots that
                look like dead unread indicators. We still reserve a
                fixed-width slot via opacity so row text doesn't shift
                when fresh items become seen. */}
            <div
              className={[
                'mt-[7px] h-2 w-2 flex-shrink-0 rounded-full bg-ss-blue',
                isFresh ? 'opacity-100' : 'opacity-0',
              ].join(' ')}
              aria-hidden="true"
            />
            <div className="flex-1 min-w-0">
              {titleText ? (
                <div className="text-sm font-semibold leading-snug text-ink-900 dark:text-[#E2E8F0]">
                  {titleText}
                </div>
              ) : null}
              <div
                className={[
                  'text-sm leading-snug',
                  titleText
                    ? 'text-ink-500 dark:text-[#94A3B8]'
                    : 'font-semibold text-ink-900 dark:text-[#E2E8F0]',
                ].join(' ')}
              >
                {bodyText}
              </div>
              <div className="mt-0.5 text-xs text-ink-400 dark:text-[#64748B]">
                {relativeTime(it.timestamp_iso)}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
